import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Fingerprint, Clock3, ShieldCheck, KeyRound, Lock, Activity } from 'lucide-react';
import { useCreateSipProfile, useUpdateSipProfile } from '../../../hooks/useSipProfiles';
import { createFormErrorHandler } from '../../../utils/form-error-handler';
import { ResourceEditor, type ResourceEditorTab, firstTabWithError } from '../../common';
import { SRTP_CRYPTO_SUITES } from '../sip-peer-editor/schema';
import {
  buildSipProfileDefaultValues,
  moveCryptoSuite,
  sipProfileFormSchema,
  toCreateInput,
  toUpdateInput,
  type SipProfile,
  type SipProfileFormData,
} from './schema';

const asOptionalNumber = (value: string | number | null | undefined) => {
  if (value === '' || value === null || value === undefined) return undefined;
  const next = Number(value);
  return Number.isNaN(next) ? undefined : next;
};

interface SipProfileEditorProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onSuccess?: () => void;
  readonly onSaved?: (profile: SipProfile) => void;
  readonly editingProfile?: SipProfile | null;
}

function FormError({ message }: { readonly message?: string }) {
  if (!message) return null;
  return <p className="mt-1 text-sm text-red-600">{message}</p>;
}

export function SipProfileEditor({ isOpen, onClose, onSuccess, onSaved, editingProfile }: SipProfileEditorProps) {
  const [activeTab, setActiveTab] = useState('identity');
  const formErrorHandler = createFormErrorHandler({ componentName: 'SipProfileEditor', showFieldErrors: true });
  const form = useForm<SipProfileFormData>({
    resolver: zodResolver(sipProfileFormSchema),
    defaultValues: buildSipProfileDefaultValues(editingProfile),
  });

  useEffect(() => {
    if (!isOpen) return;
    form.reset(buildSipProfileDefaultValues(editingProfile));
    setActiveTab('identity');
  }, [isOpen, editingProfile, form]);

  const createMutation = useCreateSipProfile();
  const updateMutation = useUpdateSipProfile();

  const handleSuccess = (response: { success: boolean; data: SipProfile }) => {
    if (!response.success) { formErrorHandler.onError(new Error('Profile was not saved')); return; }
    onSaved?.(response.data);
    onSuccess?.();
    onClose();
    form.reset(buildSipProfileDefaultValues(null));
    setActiveTab('identity');
  };

  const onSubmit = (data: SipProfileFormData) => {
    if (editingProfile) {
      const payload = toUpdateInput(data);
      updateMutation.mutate({ profileId: editingProfile.id, input: payload }, { onSuccess: handleSuccess, onError: formErrorHandler.onError });
    } else {
      const payload = toCreateInput(data);
      createMutation.mutate(payload, { onSuccess: handleSuccess, onError: formErrorHandler.onError });
    }
  };

  const tabs: ResourceEditorTab[] = useMemo(() => [
    {
      id: 'identity',
      label: 'Identity',
      icon: Fingerprint,
      fields: ['name', 'description', 'enabled'],
      render: () => (
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Identity</h3>
            <p className="text-sm text-gray-600">Basic profile details.</p>
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Profile name *</label>
              <input {...form.register('name')} type="text" className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
              <FormError message={form.formState.errors.name?.message} />
            </div>
            <div className="flex items-center md:pt-8">
              <input {...form.register('enabled')} type="checkbox" className="h-4 w-4 rounded border-gray-300 text-blue-600" />
              <label className="ml-2 text-sm text-gray-700">Enable profile</label>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
              <textarea {...form.register('description')} rows={3} className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'sessionTimers',
      label: 'Session timers',
      icon: Clock3,
      fields: ['sessionTimerMode', 'sessionExpiresDefaultSeconds', 'sessionExpiresMinSeconds', 'sessionExpiresMaxSeconds', 'sessionRefresherPreference', 'sessionTimerRejectBelowMin'],
      render: () => (
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Session timers</h3>
            <p className="text-sm text-gray-600">RFC 4028 session timer policy.</p>
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Session timer mode</label>
              <select {...form.register('sessionTimerMode')} className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white">
                <option value="disabled">Disabled</option>
                <option value="supported">Supported</option>
                <option value="required">Required</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Session refresher preference</label>
              <select {...form.register('sessionRefresherPreference')} className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white">
                <option value="">Unspecified</option>
                <option value="uas">UAS</option>
                <option value="uac">UAC</option>
                <option value="none">None</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Default SE seconds</label>
              <input {...form.register('sessionExpiresDefaultSeconds', { setValueAs: asOptionalNumber })} type="number" min="1" className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
              <FormError message={form.formState.errors.sessionExpiresDefaultSeconds?.message} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Reject below min</label>
              <input {...form.register('sessionTimerRejectBelowMin')} type="checkbox" className="h-4 w-4 rounded border-gray-300 text-blue-600" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Minimum SE seconds</label>
              <input {...form.register('sessionExpiresMinSeconds', { setValueAs: asOptionalNumber })} type="number" min="1" className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
              <FormError message={form.formState.errors.sessionExpiresMinSeconds?.message} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Maximum SE seconds</label>
              <input {...form.register('sessionExpiresMaxSeconds', { setValueAs: asOptionalNumber })} type="number" min="1" className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
              <FormError message={form.formState.errors.sessionExpiresMaxSeconds?.message} />
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'registrar',
      label: 'Registrar',
      icon: KeyRound,
      fields: ['registrarEnabled', 'registerExpiresDefaultSeconds', 'registerExpiresMinSeconds', 'registerExpiresMaxSeconds', 'registerMaxContacts', 'registerAllowWildcardContact', 'registerRequireContact'],
      render: () => {
        const registrarEnabled = form.watch('registrarEnabled');
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Registrar</h3>
              <p className="text-sm text-gray-600">REGISTER policy and contact limits.</p>
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input {...form.register('registrarEnabled')} type="checkbox" className="h-4 w-4 rounded border-gray-300 text-blue-600" />
              Enable registrar handling
            </label>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">REGISTER default seconds</label>
                <input {...form.register('registerExpiresDefaultSeconds', { setValueAs: asOptionalNumber })} disabled={!registrarEnabled} type="number" min="1" className="w-full rounded-md border border-gray-300 px-3 py-2 disabled:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                <FormError message={form.formState.errors.registerExpiresDefaultSeconds?.message} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Maximum contacts</label>
                <input {...form.register('registerMaxContacts', { setValueAs: asOptionalNumber })} disabled={!registrarEnabled} type="number" min="1" className="w-full rounded-md border border-gray-300 px-3 py-2 disabled:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                <FormError message={form.formState.errors.registerMaxContacts?.message} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">REGISTER min seconds</label>
                <input {...form.register('registerExpiresMinSeconds', { setValueAs: asOptionalNumber })} disabled={!registrarEnabled} type="number" min="1" className="w-full rounded-md border border-gray-300 px-3 py-2 disabled:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                <FormError message={form.formState.errors.registerExpiresMinSeconds?.message} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">REGISTER max seconds</label>
                <input {...form.register('registerExpiresMaxSeconds', { setValueAs: asOptionalNumber })} disabled={!registrarEnabled} type="number" min="1" className="w-full rounded-md border border-gray-300 px-3 py-2 disabled:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                <FormError message={form.formState.errors.registerExpiresMaxSeconds?.message} />
              </div>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input {...form.register('registerAllowWildcardContact')} disabled={!registrarEnabled} type="checkbox" className="h-4 w-4 rounded border-gray-300 text-blue-600" />
                Allow wildcard contact
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input {...form.register('registerRequireContact')} disabled={!registrarEnabled} type="checkbox" className="h-4 w-4 rounded border-gray-300 text-blue-600" />
                Require contact header
              </label>
            </div>
          </div>
        );
      },
    },
    {
      id: 'digest',
      label: 'Digest',
      icon: ShieldCheck,
      fields: ['digestAuthEnabled', 'digestNonceLifetimeSeconds', 'digestLockoutMaxAttempts', 'digestLockoutWindowSeconds', 'digestQop', 'digestAlgorithm'],
      render: () => {
        const digestAuthEnabled = form.watch('digestAuthEnabled');
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Digest authentication</h3>
              <p className="text-sm text-gray-600">HTTP Digest policy used for SIP auth challenges.</p>
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input {...form.register('digestAuthEnabled')} type="checkbox" className="h-4 w-4 rounded border-gray-300 text-blue-600" />
              Enable digest authentication
            </label>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nonce lifetime seconds</label>
                <input {...form.register('digestNonceLifetimeSeconds', { setValueAs: asOptionalNumber })} disabled={!digestAuthEnabled} type="number" min="1" className="w-full rounded-md border border-gray-300 px-3 py-2 disabled:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                <FormError message={form.formState.errors.digestNonceLifetimeSeconds?.message} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Lockout max attempts</label>
                <input {...form.register('digestLockoutMaxAttempts', { setValueAs: asOptionalNumber })} disabled={!digestAuthEnabled} type="number" min="1" className="w-full rounded-md border border-gray-300 px-3 py-2 disabled:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                <FormError message={form.formState.errors.digestLockoutMaxAttempts?.message} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Lockout window seconds</label>
                <input {...form.register('digestLockoutWindowSeconds', { setValueAs: asOptionalNumber })} disabled={!digestAuthEnabled} type="number" min="1" className="w-full rounded-md border border-gray-300 px-3 py-2 disabled:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                <FormError message={form.formState.errors.digestLockoutWindowSeconds?.message} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">QOP</label>
                <select {...form.register('digestQop')} disabled={!digestAuthEnabled} className="w-full rounded-md border border-gray-300 px-3 py-2 disabled:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white">
                  <option value="auth">auth</option>
                  <option value="auth-int">auth-int</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Algorithm</label>
                <select {...form.register('digestAlgorithm')} disabled={!digestAuthEnabled} className="w-full rounded-md border border-gray-300 px-3 py-2 disabled:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white">
                  <option value="MD5">MD5</option>
                  <option value="SHA-256">SHA-256</option>
                </select>
              </div>
            </div>
          </div>
        );
      },
    },
    {
      id: 'media',
      label: 'Media',
      icon: Activity,
      fields: ['srtpMode', 'sdpStripIce', 'sdpOutboundProfile', 'cryptoSuites'],
      render: () => {
        const cryptoSuites = form.watch('cryptoSuites');
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Media security</h3>
              <p className="text-sm text-gray-600">SRTP / SDES policy and outbound SDP handling.</p>
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">SRTP mode</label>
                <select {...form.register('srtpMode')} className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white">
                  <option value="disabled">Disabled</option>
                  <option value="optional">Optional</option>
                  <option value="mandatory">Mandatory</option>
                </select>
                <FormError message={form.formState.errors.srtpMode?.message} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Outbound SDP profile</label>
                <select {...form.register('sdpOutboundProfile')} className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white">
                  <option value="">Unspecified</option>
                  <option value="passthrough">Passthrough</option>
                  <option value="carrier-basic-rtp">Carrier basic RTP</option>
                </select>
              </div>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input {...form.register('sdpStripIce')} type="checkbox" className="h-4 w-4 rounded border-gray-300 text-blue-600" />
                Strip ICE candidates from SDP
              </label>
            </div>

            <div className="rounded-lg border border-gray-200 bg-white p-4 space-y-3">
              <div className="text-sm font-medium text-gray-900">Crypto suites</div>
              <p className="text-xs text-gray-500">Ordered by preference.</p>
              <div className="space-y-2">
                {cryptoSuites.map((suite, index) => (
                  <div key={suite} className="flex items-center justify-between gap-2 rounded-md border border-gray-200 px-3 py-2">
                    <span className="font-mono text-sm">{suite}</span>
                    <div className="flex items-center gap-2">
                      <button type="button" className="rounded border border-gray-300 px-2 py-1 text-xs disabled:opacity-50" disabled={index === 0} onClick={() => form.setValue('cryptoSuites', moveCryptoSuite(cryptoSuites, index, index - 1), { shouldDirty: true })}>Up</button>
                      <button type="button" className="rounded border border-gray-300 px-2 py-1 text-xs disabled:opacity-50" disabled={index === cryptoSuites.length - 1} onClick={() => form.setValue('cryptoSuites', moveCryptoSuite(cryptoSuites, index, index + 1), { shouldDirty: true })}>Down</button>
                      <button type="button" className="rounded border border-gray-300 px-2 py-1 text-xs" onClick={() => form.setValue('cryptoSuites', cryptoSuites.filter((item) => item !== suite), { shouldDirty: true })}>Remove</button>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-2 pt-2">
                <select
                  defaultValue=""
                  onChange={(event) => {
                    const next = event.target.value;
                    if (!next) return;
                    if (!cryptoSuites.includes(next)) {
                      form.setValue('cryptoSuites', [...cryptoSuites, next], { shouldDirty: true });
                    }
                    event.currentTarget.value = '';
                  }}
                  className="rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                >
                  <option value="">Add crypto suite…</option>
                  {SRTP_CRYPTO_SUITES.filter((suite) => !cryptoSuites.includes(suite)).map((suite) => (
                    <option key={suite} value={suite}>{suite}</option>
                  ))}
                </select>
              </div>
              <FormError message={form.formState.errors.cryptoSuites?.message} />
            </div>
          </div>
        );
      },
    },
  ], [form]);

  const onInvalid = (errors: typeof form.formState.errors) => {
    const target = firstTabWithError(errors, tabs);
    if (target) setActiveTab(target);
    formErrorHandler.onInvalid(errors);
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <ResourceEditor
      isOpen={isOpen}
      onClose={onClose}
      title={editingProfile ? 'Edit SIP Profile' : 'Create SIP Profile'}
      subtitle={editingProfile ? 'Update SIP profile policy sections and crypto ordering' : 'Create a SIP profile to centralize peer policy'}
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      errors={form.formState.errors}
      onSubmit={form.handleSubmit(onSubmit, onInvalid)}
      onCancel={onClose}
      isSaving={isSaving}
      saveLabel={editingProfile ? 'Update Profile' : 'Create Profile'}
      savingLabel={editingProfile ? 'Updating...' : 'Creating...'}
    />
  );
}
